/**
 * Echo Hunt -- Settings tab: per-role LLM models + a "test call" per role (P3, §16.1).
 *
 * Each role stored in `echoProfiles.modelRoles`; a blank entry means "fall back to the global model"
 * (the same model/API used by Story Explore, configured under the app's Settings > More > Services).
 */
import React, {useEffect, useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, Column, Row, Select, Text, TextInput} from "react-vcomponents";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {InfoButton} from "web-vcore";
import {store} from "../../../Store/index.js";
import {LLMApi} from "../../../Store/main/settings.js";
import {EnsureEchoProfile_Live, GetMyEchoProfile, SetModelReasoning_Live, SetModelRole_Live} from "../../../EchoHunt/Profiles_Live.js";
import {CallLLM, ResolveModelForRole, LLMCallCounter} from "../../../EchoHunt/LLM.js";
import {SetMandatoryReasoningRetryHook, liveLLMBackend} from "../../../EchoHunt/LLM_Live.js";
import {LLMRole, LLMRole_descriptions, LLMRole_labels, LLMRole_values, ReasoningEffort} from "../../../EchoHunt/Roles.js";
import {ModelCapabilities, ReasoningChoicesFor, ResolveReasoningEffort} from "../../../EchoHunt/ModelCapabilities.js";
import {GetModelCapabilities, ModelCapabilitiesAvailable} from "../../../EchoHunt/ModelCapabilities_Live.js";
import {TestCall_SchemaFor, TestCall_SystemPrompt, TestCall_UserPrompt} from "../../../EchoHunt/Prompts/TestCall.js";
import type {EchoHuntModelReasoning, EchoHuntModelRoles} from "../../../EchoHunt/Types.js";

export const ModelRolesPanel = observer_mgl(()=>{
	const settings = store.main.settings;
	const profile = GetMyEchoProfile();
	const globalModel = settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouterModel : settings.anthropicModel;

	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState<string|n>(null);

	if (profile == null) {
		return (
			<>
				<Text style={{fontWeight: "bold"}}>Per-role models</Text>
				<Text mt={5} style={{opacity: .7}}>No Echo Hunt profile exists yet -- one is needed to store the per-role model settings.</Text>
				<Row mt={5} center style={{gap: 10}}>
					<Button text={creating ? "Creating…" : "Create Echo Hunt profile"} enabled={!creating} onClick={async()=>{
						setCreating(true);
						setCreateError(null);
						try {
							await EnsureEchoProfile_Live();
						} catch (ex) {
							setCreateError(`${ex}`);
						}
						setCreating(false);
					}}/>
					{creating && <ProgressSpinner/>}
				</Row>
				{createError != null && <Text mt={5} style={{color: "red"}}>{createError}</Text>}
			</>
		);
	}

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Per-role models</Text>
				<InfoButton ml={5} text={[
					`Each Echo Hunt pipeline step ("role") can use its own model.`,
					``,
					`Leave a role blank to use the global model, which is currently: "${globalModel}"`,
					`(configured under Settings > More > Services: ${settings.llm_mainAPI == LLMApi.openRouter ? "OpenRouter" : "Anthropic"})`,
					``,
					`Suggested defaults (§16.1): a cheap model for indexing, generation, reshaping, rewriting and judging;`,
					`a top-tier model for the weaver; and a held-out judge from a DIFFERENT model family than the loop judge.`,
				].join("\n")}/>
			</Row>
			<Text mt={5} style={{opacity: .7}}>Changes save when you leave the field (or immediately, for the dropdowns).</Text>
			<Row mt={8}>
				<Text style={{fontWeight: "bold"}}>Reasoning effort</Text>
				<InfoButton ml={5} text={[
					"Some models refuse a request that turns reasoning off, with:",
					`  "Reasoning is mandatory for this endpoint and cannot be disabled"`,
					"",
					`The effort dropdown is read from each model's own metadata (${ModelCapabilitiesAvailable() ? "OpenRouter's per-model endpoint" : "unavailable on this API type, so only \"auto\" and the fixed list are offered"}).`,
					"",
					`"auto" (the default) means: if the model REQUIRES reasoning, use its own default effort; otherwise send`,
					`exactly what was sent before. That is why every model that works today keeps working unchanged.`,
					"",
					"Set an effort explicitly to override that -- higher efforts generally mean better quality at more tokens" ,
					"and more latency. An unsupported choice is downgraded to the nearest supported effort rather than sent.",
				].join("\n")}/>
			</Row>
			<Column mt={10} style={{gap: 6}}>
				{LLMRole_values.map(role=>
					<ModelRoleRow key={role} role={role} profileID={profile.id} modelRoles={profile.modelRoles} modelReasoning={profile.modelReasoning} globalModel={globalModel}/>,
				)}
			</Column>
			{/* TODO(EchoHunt P13): a "reset all roles" action, and a per-round cost summary of the counters */}
		</>
	);
});

const ModelRoleRow = observer_mgl((props: {role: LLMRole, profileID: string, modelRoles: EchoHuntModelRoles|n, modelReasoning: EchoHuntModelReasoning|n, globalModel: string})=>{
	const {role, profileID, modelRoles, modelReasoning, globalModel} = props;
	const override = modelRoles?.[role] ?? "";
	const resolvedModel = ResolveModelForRole(modelRoles, role, globalModel);
	const requestedEffort = modelReasoning?.[role] ?? null;

	const [testState, setTestState] = useState<{running: boolean, result?: string, error?: string}|null>(null);
	/** The model's metadata, fetched once per (resolved) model id. Null while loading, and `UnknownCapabilities`
	 * when the fetch failed -- either way the dropdown still works, it just offers the fixed list. */
	const [capabilities, setCapabilities] = useState<ModelCapabilities|null>(null);

	useEffect(()=>{
		let cancelled = false;
		setCapabilities(null);
		GetModelCapabilities(resolvedModel).then(result=>{
			if (!cancelled) setCapabilities(result);
		});
		return ()=>{ cancelled = true; };
	}, [resolvedModel]);

	const resolvedReasoningEffort = capabilities == null ? null :
		ResolveReasoningEffort(capabilities, {requested: null});
	/** What "auto" will actually do for this model, so the row explains itself rather than showing a bare
	 * "auto" that means different things per model. */
	const autoDescription = capabilities == null
		? "reading model info…"
		: resolvedReasoningEffort!.note;
	const choices = ReasoningChoicesFor(capabilities);

	return (
		<Column style={{gap: 1}}>
			<Row style={{alignItems: "center"}}>
				<Text style={{width: 110}} title={LLMRole_descriptions[role]}>{LLMRole_labels[role]}</Text>
				<TextInput style={{width: 260}} value={override} placeholder={globalModel || "(global model)"}
					onChange={val=>{
						SetModelRole_Live(profileID, role, val).catch(ex=>console.error(`Failed to save model for role "${role}":`, ex));
					}}/>
				<Text ml={8} style={{opacity: .55, fontSize: 11, width: 190}}>
					{override.trim().length > 0 ? "override" : `uses global: ${resolvedModel || "(unset)"}`}
				</Text>
				<Select ml={5} displayType="dropdown" style={{width: 130}} value={requestedEffort ?? AUTO}
					options={choices.map(a=>({name: a == null ? `auto (${resolvedReasoningEffort?.effort})` : a, value: a ?? AUTO}))}
					onChange={val=>{
						const effort = val == AUTO ? null : val as ReasoningEffort;
						SetModelReasoning_Live(profileID, role, effort).catch(ex=>console.error(`Failed to save reasoning for role "${role}":`, ex));
					}}/>
				<Button ml={5} text={testState?.running ? "Testing…" : "Test"} enabled={!testState?.running} onClick={async()=>{
					setTestState({running: true});
					const counter = new LLMCallCounter();
					let autoRetried = false;
					// the backend can only DISCOVER a mandatory-reasoning requirement on an API type with no metadata
					// endpoint, so it retries once; say so rather than letting the first failure look like a real one
					SetMandatoryReasoningRetryHook(()=>{ autoRetried = true; });
					try {
						const result = await CallLLM(liveLLMBackend, {
							role,
							model: resolvedModel,
							system: TestCall_SystemPrompt(),
							user: TestCall_UserPrompt(role),
							json: TestCall_SchemaFor(role),
							counter,
						});
						const retryNote = autoRetried ? " [reasoning was switched on automatically: this model requires it]" : "";
						const summary = result.error != null
							? `FAILED after ${result.attempts} attempt(s): ${result.error}`
							: `OK (${result.model}, ${result.ms} ms, ${result.outputTokens} output tokens): ${JSON.stringify(result.parsed)}${retryNote}`;
						setTestState({running: false, result: summary, error: result.error ?? undefined});
					} catch (ex) {
						setTestState({running: false, error: `${ex}`});
					} finally {
						SetMandatoryReasoningRetryHook(null);
					}
				}}/>
				{testState?.running && <ProgressSpinner ml={5}/>}
			</Row>
			{/*<Text ml={118} style={{opacity: .45, fontSize: 11}}>
				{requestedEffort == null ? autoDescription : `reasoning: ${requestedEffort} (chosen)`}
			</Text>*/}
			{testState != null && !testState.running && (testState.error != null
				? <Text sel ml={118} style={{color: "red", fontSize: 12}}>{testState.error}</Text>
				: <Text sel ml={118} style={{fontSize: 12}}>{testState.result}</Text>)}
		</Column>
	);
});

/** Select value meaning "auto" (null cannot be a Select value). */
const AUTO = "__auto__";
